/**
 * Nền tảng AI chọn được cho nhân viên AI / cấu hình AI — khớp backend
 * `provider-registry.ts` (SUPPORTED_PROVIDER_IDS). Danh sách model đến từ API
 * (`availableProviders`); file này chỉ lo thứ tự, logo và cảnh báo nền tảng cũ.
 */
import { i18n } from './i18n'
import { BRAND_LOGOS, type BrandKey } from './brand-logos'

export const SUPPORTED_AI_PROVIDERS = ['openai', 'anthropic', 'gemini', 'xai', 'deepseek'] as const
export type SupportedAiProvider = (typeof SUPPORTED_AI_PROVIDERS)[number]

const LOGO_OF: Record<SupportedAiProvider, BrandKey> = {
  openai: 'openai',
  anthropic: 'claude',
  gemini: 'gemini',
  xai: 'grok',
  deepseek: 'deepseek',
}

/** Cảnh báo cho nhân viên AI / cấu hình đang dùng nền tảng đã bỏ (vd MiniMax). */
export function legacyProviderWarning(): string {
  return i18n.t('aiMisc:aiProviders.legacyWarning')
}

/** @deprecated Giá trị tiếng Việt cố định (giữ tương thích nơi gọi cũ) — dùng `legacyProviderWarning()` để có bản dịch. */
export const LEGACY_PROVIDER_WARNING = 'Nền tảng không còn hỗ trợ — vui lòng chọn lại' // i18n-ignore

export function isSupportedAiProvider(id: string | null | undefined): id is SupportedAiProvider {
  return !!id && (SUPPORTED_AI_PROVIDERS as readonly string[]).includes(id)
}

/** true nếu đã chọn một nền tảng cụ thể nhưng nền tảng đó không còn hỗ trợ. */
export function isLegacyAiProvider(id: string | null | undefined): boolean {
  return !!id && !isSupportedAiProvider(id)
}

/** Đường dẫn logo tự host của nền tảng (undefined nếu không phải nền tảng hỗ trợ). */
export function aiProviderLogo(id: string): string | undefined {
  return isSupportedAiProvider(id) ? BRAND_LOGOS[LOGO_OF[id]].src : undefined
}

/** Lọc + sắp đúng thứ tự ChatGPT, Claude, Gemini, Grok, DeepSeek (phòng khi API trả thêm). */
export function supportedProvidersOf<T extends { id: string }>(list: T[]): T[] {
  return SUPPORTED_AI_PROVIDERS.flatMap((id) => list.filter((p) => p.id === id))
}

/** Id kênh đang ghi đè AI bằng nền tảng cũ (vd MiniMax) — hiện cảnh báo + cho gỡ (D6). */
export function legacyOverrideChannelIds(overrides: Record<string, { provider?: string } | undefined>): string[] {
  return Object.entries(overrides).filter(([, o]) => isLegacyAiProvider(o?.provider)).map(([id]) => id)
}

/** Nền tảng cũ còn model tuỳ chỉnh (chỉ được gỡ, không thêm mới). */
export function legacyCustomModelProviders(models: Record<string, unknown[] | undefined>): string[] {
  return Object.entries(models).filter(([id, list]) => isLegacyAiProvider(id) && (list?.length ?? 0) > 0).map(([id]) => id)
}

/** Nền tảng cũ tổ chức còn lưu API key — hiện ở thẻ API key để xoá được. */
export function legacyKeyProviders(providerKeys: Record<string, boolean> | undefined): string[] {
  return Object.entries(providerKeys ?? {}).filter(([id, has]) => has && isLegacyAiProvider(id)).map(([id]) => id)
}

/** Tên hiển thị nền tảng cũ. */
export function legacyProviderName(id: string): string {
  return i18n.t('aiMisc:aiProviders.legacyName', { name: id === 'minimax' ? 'MiniMax' : id })
}
